import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import { getProgramGroups } from '../../../api/strapi';
import { parseDate, preparePrice } from '../../../helpers/lib';

import Section from '../../../shared/Section/Section';
import ClockIcon from '../../../shared/Feature/icons/ClockIcon';
import CalendarIcon from '../../../shared/Feature/icons/CalendarIcon';
import Status from '../../../shared/Status/Status';
import ModalBlock from '../../../shared/ModalBlock/ModalBlock';
import ProgramRequestModal from '../../../shared/Modals/ProgramRequestModal';

const PROGRAM = 'JS';
function EducationOnlineFormat({ title, list, images, requestLeadData }) {
  const isSm = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [groups, setGroups] = useState(list);
  const [requestModal, setRequestModal] = useState(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  useEffect(() => {
    const ac = new AbortController();
    getProgramGroups(ac, PROGRAM, null, true).then((groups) => {
      return setGroups(
        (prevState) =>
          groups &&
          prevState?.reduce((acc, curr) => {
            const dbGroup = groups.find((el) => {
              return el.location === curr.location;
            });
            const startDate = parseDate(dbGroup?.startDate);
            startDateRef.current = startDate;
            const endDate = parseDate(dbGroup?.endDate);
            endDateRef.current = endDate;
            const examDeadline = parseDate(dbGroup?.examDeadline);

            const fullFormat = {
              ...curr,
              ...dbGroup,
              courseDates:
                startDate &&
                `${startDate[1]} ${startDate[2]} – ${endDate[1]} ${endDate[2]}`,
              examDeadline:
                examDeadline && `${examDeadline[1]} ${examDeadline[2]}`,
              startDate,
              endDate,
            };
            return [...acc, fullFormat];
          }, []),
      );
    });
  }, []);
  return (
    <Section title={title} theme="lightPurple" bg={'online-formats'}>
      <Box
        sx={{
          position: 'relative',
          overflowX: { xl: 'hidden', lg: 'scroll', md: 'scroll', sm: 'hidden' },
        }}
      >
        <Box
          container
          sx={{ display: 'flex', gap: { xs: 3, lg: 6 }, flexWrap: 'wrap' }}
        >
          {(groups || list).map(
            ({
              name,
              location,
              profession,
              duration,
              duration1,
              schedule,
              courseDates,
              startDate,
              endDate,
              examDeadline,
              price,
              placesLeft,
              ymClickEventType,
              ymSentEvent,
              myTargetSentEvent,
              image,
            }) => (
              <Paper
                key={name}
                elevation={0}
                sx={{
                  width: { xs: '100%', lg: 536 },
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 'fit-content', lg: 300 },
                    // backgroundColor: 'darkgray',
                    px: { xs: 2, lg: 4 },
                    py: { xs: 2, lg: 3 },
                    position: 'relative',
                    overflow: 'hidden',
                    color: 'kit.text.mainInverse',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      zIndex: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <GatsbyImage
                      image={getImage(images[`devPage/${image}`])}
                      style={{
                        height: '100%',
                        objectFit: 'cover',
                        width: 'auto',
                      }}
                      alt={name}
                      loading="auto"
                      layout="fullWidth"
                    />
                  </Box>
                  <Box
                    sx={{
                      zIndex: 1,
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        // justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        sx={{
                          padding: '5px 10px 5px 10px',
                          display: 'inline-block',
                          backgroundColor: '#FFFFFF24',
                          borderRadius: '4px',
                        }}
                        fontSize={isSm ? 11 : 12}
                        textAlign="left"
                      >
                        {`<${profession}>`}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          ml: 'auto',
                        }}
                      >
                        <CalendarIcon fillColor="#4520AB" />
                        <Typography variant="desktopP2">{duration}</Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant={isSm ? 'mobileH4' : 'desktopH4'}
                      fontWeight={700}
                      mt={4}
                    >
                      {name}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        mt: 1.5,
                      }}
                    >
                      <ClockIcon strokeColor="#4520AB" />
                      <Typography variant={isSm ? 'mobileP3' : 'desktopP2'}>
                        {duration1}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        mt: 1.5,
                      }}
                    >
                      <CalendarIcon fillColor="#4520AB" />
                      <Typography variant={isSm ? 'mobileP3' : 'desktopP2'}>
                        {schedule}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 'auto' }}>
                      <Typography variant="desktopP1">
                        {price ? (
                          `От ${preparePrice(price.bankLoanPricePerMonth)}
                        ₽/месяц`
                        ) : (
                          <Skeleton width={120} height={40} />
                        )}
                      </Typography>
                      <Typography
                        variant={isSm ? 'mobileP3' : 'desktopP3'}
                        color="#FFFFFFB2"
                      >
                        {price ? (
                          `или сразу ${preparePrice(price.fullPrice)}₽`
                        ) : (
                          <Skeleton width={120} height={20} />
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    height: { xs: 165, lg: 213 },
                    px: { xs: 2, lg: 4 },
                    pt: 2,
                    pb: { xs: 2, lg: 3 },
                    mt: { xs: 0.5, lg: 2 },
                  }}
                >
                  <Typography
                    variant="desktopH4"
                    color={'kit.text.h'}
                    sx={{ fontSize: { xs: 16, lg: 22 } }}
                  >
                    {courseDates}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: { xs: 0.5, lg: 1 },
                      mt: { xs: 1.5, lg: 2 },
                    }}
                  >
                    <Typography variant="desktopP3" color={'kit.text.main'}>
                      Вступительное интервью
                    </Typography>
                    {examDeadline ? (
                      <Typography
                        variant="desktopP3"
                        color={'kit.text.secondary'}
                      >
                        до {examDeadline}
                      </Typography>
                    ) : (
                      <Skeleton height={20} width={100} />
                    )}
                  </Box>
                  <Box sx={{ mt: 'auto' }}>
                    <Status
                      styleProp={{
                        color: 'kit.text.secondary',
                        backgroundColor: 'kit.background.middlepurple',
                        borderRadius: 1,
                        border: 'none',
                        textTransform: 'none',
                        fontWeight: 400,
                        width: 'fit-content',
                        fontFamily: 'Inter',
                        fontSize: { xs: 11, lg: 12 },
                        padding: { xs: '2px 4px 4px', xl: '5px 8px' },
                        minWidth: 78,
                        height: 'fit-content',
                      }}
                    >
                      {(placesLeft && `<осталось ${placesLeft} мест>`) || (
                        <Skeleton />
                      )}
                    </Status>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      size={isSm ? 'small' : 'medium'}
                      onClick={() =>
                        setRequestModal({
                          courseName: name,
                          startDate,
                          endDate,
                          program: `${PROGRAM}_${location?.toLowerCase()}`,
                          ymClickEventType,
                          ymSentEvent,
                          myTargetSentEvent,
                        })
                      }
                    >
                      Записаться на курс
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ),
          )}
        </Box>
      </Box>
      <ModalBlock
        isOpen={!!requestModal}
        handleClose={() => setRequestModal(false)}
        ymClickEventType={requestModal?.ymClickEventType}
      >
        <ProgramRequestModal
          extraLeadData={{
            ...requestLeadData,
            program: requestModal?.program,
          }}
          startDate={requestModal?.startDate}
          endDate={requestModal?.endDate}
          // date={group?.startDate}
          formData={{
            subtitle: title?.join(' '),
            title: requestModal?.courseName,
          }}
          ymSubmitEventTypes={[requestModal?.ymSentEvent]}
          myTargetSubmitEventTypes={[requestModal?.myTargetSentEvent]}
        />
      </ModalBlock>
    </Section>
  );
}

export default EducationOnlineFormat;
